import { type Dispatch, type SetStateAction, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormattedMessage, useIntl } from 'react-intl';
import { Modal, Button } from '@printdeal/ui-library';
import { useMutation } from '@tanstack/react-query';
import { ErrorManagement } from '@printdeal/error-management/react';
import { ServiceFactory } from '../../../services/ServiceFactory';

import {
  dispatchDataLayerEventOnModalOpen,
  dispatchDataLayerEventOnFormCompleted,
  dispatchDataLayerEventOnModalClose,
  EventsForClosure,
  FeedbackFormValues,
  feedbackFormSchema,
  getDefaultFormValues,
} from './helpers/conversation-loop';

import FeedbackForm from './FeedbackForm';

interface FeedbackModalProps {
  setFeedbackModalOpen: Dispatch<SetStateAction<boolean>>;
  handleCustomerCareLinkMouseLeave?: () => void;
  handleMobileCustomerServiceLinkClickClose?: () => void;
}

const ConversationLoop = ({
  setFeedbackModalOpen,
  handleCustomerCareLinkMouseLeave,
  handleMobileCustomerServiceLinkClickClose,
}: FeedbackModalProps) => {
  const intl = useIntl();
  const handleCloseModal = (eventName: EventsForClosure) => {
    dispatchDataLayerEventOnModalClose(eventName);
    setFeedbackModalOpen(false);
    handleCustomerCareLinkMouseLeave?.();
    handleMobileCustomerServiceLinkClickClose?.();
  };

  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackFormSchema),
    mode: 'onChange',
    defaultValues: getDefaultFormValues(),
  });

  const {
    reset,
    formState: { isSubmitting, isValid = true },
  } = form;

  const sendFeedbackMutation = async (values: FeedbackFormValues) => {
    const customerApi = ServiceFactory.createCustomerService();
    await customerApi.addConversationToCustomer(values);
  };

  const mutation = useMutation({
    mutationFn: (values: FeedbackFormValues) => sendFeedbackMutation(values),
    onSuccess: () => {
      reset();
      dispatchDataLayerEventOnFormCompleted();
      setTimeout(() => handleCloseModal('modal_close_after_success'), 5000);
    },
    onError: (error, values) => {
      ErrorManagement.exception({
        exception: error,
        extra: {
          body: values.body,
          feedbackType: values.feedbackType,
        },
        tags: {
          fileLocation: 'ConversationLoop.tsx',
          featureFunction: 'onSubmit',
        },
        fingerprint: ['ConversationLoop-onSubmit'],
      });
    },
  });

  const onSubmit = (values: FeedbackFormValues) => {
    mutation.mutate(values);
  };

  useEffect(() => {
    dispatchDataLayerEventOnModalOpen();
  }, []);

  return (
    <Modal
      isOpen
      onClose={() => { handleCloseModal('modal_close_generic'); }}
      title={(
        <FormattedMessage
          id="general.feedback.modal.title.label"
          defaultMessage="Share your feedback and help us improve"
        />
      )}
      dismissLabel={intl.formatMessage({
        id: 'general.alert.dismiss',
        defaultMessage: 'Dismiss',
      })}
    >
      <Modal.Content className="pd:h-[500px] pd:overflow-y-auto">
        <FeedbackForm
          form={form}
          mutation={mutation}
          onSubmit={onSubmit}
        />
      </Modal.Content>
      <Modal.Footer className="pd:flex pd:flex-col pd:items-end pd:gap-4 pd:text-right pd:md:flex-row-reverse">
        {mutation.status !== 'success' && (
          <Button
            type="submit"
            isLoading={isSubmitting || mutation.isPending}
            disabled={!isValid || isSubmitting || mutation.isPending}
            form="feedbackmodalForm"
          >
            <FormattedMessage
              id="general.feedback.modal.send"
              defaultMessage="Send"
            />
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ConversationLoop;
